import React, { useState, useEffect } from 'react';
import { getHrisGaji, getHrisGajiById, createHrisGaji, batchHrisGaji, updateHrisGaji, deleteHrisGaji, getKaryawan } from '../../api';
import { Plus, Edit2, Trash2, DollarSign, Search, Building, CreditCard, CalendarRange, CalendarDays, FileText } from 'lucide-react';

const JENIS_LIST = [
  { value: 'Bulanan', label: 'Gaji Bulanan', icon: CalendarRange, color: 'var(--cyan-500)' },
  { value: 'Mingguan', label: 'Gaji Mingguan', icon: CalendarDays, color: '#8965e0' },
];

const emptyForm = {
  karyawan_id: '', jenis_gaji: 'Bulanan',
  gaji_pokok: 0, tunjangan_tetap: 0, tunjangan_tidak_tetap: 0, potongan_tetap: 0,
  tunjangan_jabatan: 0, insentif_keahlian_ac: 0, insentif_project: 0,
  potongan_alpa: 0, potongan_kasbon: 0,
  tunjangan_transport_bbm: 0, tunjangan_sewa_kendaraan: 0,
  bpjs_kesehatan: 0, bpjs_ketenagakerjaan: 0,
  bpjs_kesehatan_perusahaan: 0, bpjs_ketenagakerjaan_perusahaan: 0,
  metode_pembayaran: 'Transfer', bank: '', no_rekening: '', keterangan: ''
};

// Baris kosong untuk grid input ala Excel (kolom komponen utama)
const emptyGridRow = () => ({
  karyawan_id: '',
  gaji_pokok: '', tunjangan_jabatan: '', insentif_keahlian_ac: '', insentif_project: '',
  potongan_alpa: '', potongan_kasbon: '',
  tunjangan_transport_bbm: '', tunjangan_sewa_kendaraan: '',
  keterangan: '',
});

export default function HrisGaji() {
  const [data, setData] = useState([]);
  const [karyawan, setKaryawan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterJenis, setFilterJenis] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Grid input ala Excel (mode tambah baru)
  const [gridJenis, setGridJenis] = useState('Bulanan');
  const [gridRows, setGridRows] = useState([emptyGridRow()]);

  const fetchData = () => {
    setLoading(true);
    Promise.all([getHrisGaji({ jenis: filterJenis || undefined }), getKaryawan()])
      .then(([dRes, kRes]) => { setData(dRes.data); setKaryawan(kRes.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [filterJenis]);

  const openCreate = () => {
    setEditing(null);
    setGridJenis('Bulanan');
    setGridRows([emptyGridRow()]);
    setShowModal(true);
  };

  // ==== Grid Excel helpers ====
  const updateGridRow = (idx, field, value) => {
    const rows = [...gridRows];
    rows[idx] = { ...rows[idx], [field]: value };
    setGridRows(rows);
  };
  const addGridRow = () => setGridRows([...gridRows, emptyGridRow()]);
  const removeGridRow = (idx) => {
    if (gridRows.length <= 1) return;
    setGridRows(gridRows.filter((_, i) => i !== idx));
  };

  const handleBatchSubmit = async () => {
    const valid = gridRows.filter(r => r.karyawan_id);
    if (valid.length === 0) { alert('Pilih minimal 1 karyawan'); return; }
    // Validasi: karyawan tidak dobel di grid
    const ids = valid.map(r => Number(r.karyawan_id));
    if (new Set(ids).size !== ids.length) { alert('Ada karyawan yang sama di lebih dari 1 baris'); return; }
    try {
      const items = valid.map(r => ({
        karyawan_id: Number(r.karyawan_id),
        jenis_gaji: gridJenis,
        gaji_pokok: Number(r.gaji_pokok) || 0,
        tunjangan_jabatan: Number(r.tunjangan_jabatan) || 0,
        insentif_keahlian_ac: Number(r.insentif_keahlian_ac) || 0,
        insentif_project: Number(r.insentif_project) || 0,
        potongan_alpa: Number(r.potongan_alpa) || 0,
        potongan_kasbon: Number(r.potongan_kasbon) || 0,
        tunjangan_transport_bbm: Number(r.tunjangan_transport_bbm) || 0,
        tunjangan_sewa_kendaraan: Number(r.tunjangan_sewa_kendaraan) || 0,
        keterangan: (r.keterangan || '').trim(),
      }));
      const res = await batchHrisGaji({ items });
      setShowModal(false);
      fetchData();
      alert(res.data?.message || 'Data gaji berhasil disimpan');
    } catch (e) {
      alert(e.response?.data?.error || 'Gagal menyimpan data gaji');
    }
  };

  const openEdit = async (id) => {
    try {
      const res = await getHrisGajiById(id);
      const d = res.data;
      setEditing(d);
      setForm({
        karyawan_id: d.karyawan_id, jenis_gaji: d.jenis_gaji || 'Bulanan',
        gaji_pokok: d.gaji_pokok, tunjangan_tetap: d.tunjangan_tetap,
        tunjangan_tidak_tetap: d.tunjangan_tidak_tetap, potongan_tetap: d.potongan_tetap,
        tunjangan_jabatan: d.tunjangan_jabatan, insentif_keahlian_ac: d.insentif_keahlian_ac,
        insentif_project: d.insentif_project, potongan_alpa: d.potongan_alpa,
        potongan_kasbon: d.potongan_kasbon,
        tunjangan_transport_bbm: d.tunjangan_transport_bbm, tunjangan_sewa_kendaraan: d.tunjangan_sewa_kendaraan,
        bpjs_kesehatan: d.bpjs_kesehatan, bpjs_ketenagakerjaan: d.bpjs_ketenagakerjaan,
        bpjs_kesehatan_perusahaan: d.bpjs_kesehatan_perusahaan,
        bpjs_ketenagakerjaan_perusahaan: d.bpjs_ketenagakerjaan_perusahaan,
        metode_pembayaran: d.metode_pembayaran, bank: d.bank, no_rekening: d.no_rekening,
        keterangan: d.keterangan || ''
      });
      setShowModal(true);
    } catch (e) { alert('Gagal memuat data'); }
  };

  const handleSubmit = async () => {
    if (!form.karyawan_id) { alert('Pilih karyawan'); return; }
    try {
      if (editing) {
        await updateHrisGaji(editing.id, form);
      } else {
        await createHrisGaji(form);
      }
      setShowModal(false);
      fetchData();
    } catch (e) {
      const msg = e.response?.data?.error || 'Gagal menyimpan';
      alert(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus data gaji ini?')) return;
    try { await deleteHrisGaji(id); fetchData(); } catch (e) { alert('Gagal menghapus'); }
  };

  const fmtRupiah = (v) => `Rp ${Number(v || 0).toLocaleString('id-ID')}`;

  const isMingguan = form.jenis_gaji === 'Mingguan';

  const hitungTotal = (d) => {
    if ((d.jenis_gaji || 'Bulanan') === 'Mingguan') {
      return Number(d.tunjangan_transport_bbm || 0) + Number(d.tunjangan_sewa_kendaraan || 0);
    }
    return Number(d.gaji_pokok || 0) + Number(d.tunjangan_tetap || 0) + Number(d.tunjangan_tidak_tetap || 0)
      + Number(d.tunjangan_jabatan || 0) + Number(d.insentif_keahlian_ac || 0) + Number(d.insentif_project || 0)
      - Number(d.potongan_tetap || 0) - Number(d.potongan_alpa || 0) - Number(d.potongan_kasbon || 0)
      - Number(d.bpjs_kesehatan || 0) - Number(d.bpjs_ketenagakerjaan || 0);
  };

  const hitungPenerimaan = (d) => {
    if ((d.jenis_gaji || 'Bulanan') === 'Mingguan') {
      return Number(d.tunjangan_transport_bbm || 0) + Number(d.tunjangan_sewa_kendaraan || 0);
    }
    return Number(d.gaji_pokok || 0) + Number(d.tunjangan_tetap || 0) + Number(d.tunjangan_tidak_tetap || 0)
      + Number(d.tunjangan_jabatan || 0) + Number(d.insentif_keahlian_ac || 0) + Number(d.insentif_project || 0);
  };

  const hitungPotongan = (d) => {
    if ((d.jenis_gaji || 'Bulanan') === 'Mingguan') return 0;
    return Number(d.potongan_tetap || 0) + Number(d.potongan_alpa || 0) + Number(d.potongan_kasbon || 0)
      + Number(d.bpjs_kesehatan || 0) + Number(d.bpjs_ketenagakerjaan || 0);
  };

  const totalData = data.length;
  const totalBulanan = data.filter(d => (d.jenis_gaji || 'Bulanan') === 'Bulanan').length;
  const totalMingguan = data.filter(d => (d.jenis_gaji || 'Bulanan') === 'Mingguan').length;
  const totalGaji = data.reduce((sum, d) => sum + hitungTotal(d), 0);

  const filtered = data.filter(d => !search
    || d.nama?.toLowerCase().includes(search.toLowerCase())
    || (d.keterangan || '').toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="page-loading"><div className="spinner-lg" /></div>;

  return (
    <div className="page-container">
      <div className="page-header" style={{ borderBottom: '2px solid var(--gray-100)', paddingBottom: 16, marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--gray-600)' }}>
          <DollarSign size={22} style={{ marginRight: 8, verticalAlign: 'middle', color: 'var(--cyan-500)' }} />
          Data Komponen Gaji
        </h2>
        <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', margin: 0 }}>Konsep penggajian 2 jenis — Bulanan & Mingguan</p>
      </div>

      {/* Stat Cards */}
      {data.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid var(--info)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Total Data Gaji</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--gray-600)', lineHeight: 1.2 }}>{totalData}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid var(--cyan-500)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Bulanan</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--cyan-700)', lineHeight: 1.2 }}>{totalBulanan}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #8965e0' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Mingguan</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#5b3fc4', lineHeight: 1.2 }}>{totalMingguan}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #2dce89' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Total Estimasi Gaji</div>
            <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#065f46', lineHeight: 1.2 }}>{fmtRupiah(totalGaji)}</div>
          </div>
        </div>
      )}

      {/* Toolbar: Tambah | Filter Jenis | Search */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
        padding: '12px 16px', background: '#fff', borderRadius: 12,
        boxShadow: '0 0 2rem 0 rgba(136,152,170,0.1)',
        flexWrap: 'wrap',
      }}>
        <button className="btn btn-primary" onClick={openCreate} style={{ whiteSpace: 'nowrap' }}>
          <Plus size={16} /> Tambah Gaji
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          {[{ value: '', label: 'Semua' }, ...JENIS_LIST].map(j => (
            <button
              key={j.value || 'all'}
              onClick={() => setFilterJenis(j.value)}
              style={{
                padding: '7px 14px', borderRadius: 8, border: '1px solid var(--gray-200)',
                fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                background: filterJenis === j.value ? (j.value === 'Mingguan' ? '#8965e0' : (j.value === 'Bulanan' ? 'var(--cyan-500)' : 'var(--gray-600)')) : '#fff',
                color: filterJenis === j.value ? '#fff' : 'var(--gray-500)',
                transition: 'all 0.2s',
              }}
            >
              {j.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 200, maxWidth: 320 }}>
          <div className="search-box" style={{ width: '100%', margin: 0 }}>
            <Search size={16} />
            <input placeholder="Cari berdasarkan nama..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>
          Menampilkan <strong style={{ color: 'var(--gray-600)' }}>{filtered.length}</strong>/{totalData}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="page-loading">Tidak ada data gaji</div>
      ) : (
        <div className="regions-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))' }}>
          {filtered.map(d => {
            const mingguan = (d.jenis_gaji || 'Bulanan') === 'Mingguan';
            const penerimaan = hitungPenerimaan(d);
            const potongan = hitungPotongan(d);
            const total = hitungTotal(d);
            return (
              <div key={d.id} className="region-card" style={{ borderTop: `3px solid ${mingguan ? '#8965e0' : 'var(--cyan-500)'}` }}>
                <div className="region-card-header" style={{
                  background: '#fff', borderBottom: '1px solid var(--gray-100)',
                  padding: '14px 16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: mingguan ? 'linear-gradient(135deg, #8965e0, #5b3fc4)' : 'linear-gradient(135deg, var(--cyan-500), var(--cyan-700))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                    }}>
                      {mingguan ? <CalendarDays size={20} /> : <DollarSign size={20} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ fontSize: '0.95rem', margin: 0, color: 'var(--gray-600)' }}>{d.nama}</h3>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4,
                        padding: '2px 10px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 700,
                        background: mingguan ? '#ede9fe' : '#e0f2fe',
                        color: mingguan ? '#5b3fc4' : 'var(--cyan-700)',
                      }}>
                        {mingguan ? 'MINGGUAN' : 'BULANAN'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="region-card-body" style={{ padding: '12px 16px' }}>
                  {mingguan ? (
                    <>
                      <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Transport BBM:</strong> {fmtRupiah(d.tunjangan_transport_bbm)}</div>
                      <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Sewa Kendaraan:</strong> {fmtRupiah(d.tunjangan_sewa_kendaraan)}</div>
                    </>
                  ) : (
                    <>
                      <div className="region-detail"><strong style={{ fontWeight: 700, color: 'var(--gray-600)' }}>Gaji Pokok:</strong> <span style={{ fontWeight: 700 }}>{fmtRupiah(d.gaji_pokok)}</span></div>
                      <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Tunj. Jabatan:</strong> {fmtRupiah(d.tunjangan_jabatan)}</div>
                      <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Insentif Keahlian (AC):</strong> {fmtRupiah(d.insentif_keahlian_ac)}</div>
                      <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Insentif Project:</strong> {fmtRupiah(d.insentif_project)}</div>
                      <div className="region-detail" style={{ borderTop: '1px dashed var(--gray-200)', paddingTop: 6, marginTop: 6 }}>
                        <strong style={{ color: '#f5365c' }}>Pot. Alpa:</strong> <span style={{ color: '#f5365c' }}>{fmtRupiah(d.potongan_alpa)}</span>
                      </div>
                      <div className="region-detail"><strong style={{ color: '#f5365c' }}>Pot. Kasbon:</strong> <span style={{ color: '#f5365c' }}>{fmtRupiah(d.potongan_kasbon)}</span></div>
                    </>
                  )}
                  {/* Mingguan hanya punya 3 kolom: Transport BBM, Sewa Kendaraan, Total */}
                  {!mingguan && (
                    <div className="region-detail" style={{
                      borderTop: '1px dashed var(--gray-200)', paddingTop: 6, marginTop: 6,
                      fontSize: '0.78rem', color: 'var(--gray-400)',
                    }}>
                      Penerimaan: <strong style={{ color: '#065f46' }}>{fmtRupiah(penerimaan)}</strong>
                      {' '}· Potongan: <strong style={{ color: '#f5365c' }}>{fmtRupiah(potongan)}</strong>
                    </div>
                  )}
                  <div className="region-detail" style={{
                    borderTop: '2px solid var(--cyan-200)', paddingTop: 8, marginTop: 8,
                    background: mingguan ? '#f5f3ff' : 'var(--accent-tint-bg)', borderRadius: 8, padding: '10px 12px',
                    marginBottom: 0,
                  }}>
                    <strong style={{ color: mingguan ? '#5b3fc4' : 'var(--cyan-700)' }}>Total Diterima:</strong>
                    <span style={{ fontWeight: 700, color: mingguan ? '#5b3fc4' : 'var(--cyan-700)', fontSize: '1rem' }}>{fmtRupiah(total)}</span>
                  </div>
                  {d.keterangan && (
                    <div className="region-detail" style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 8, marginTop: 8, alignItems: 'flex-start' }}>
                      <FileText size={14} className="text-muted" />
                      <span><strong>Keterangan:</strong> {d.keterangan}</span>
                    </div>
                  )}
                  {d.bank && (
                    <div className="region-detail" style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 8, marginTop: 8 }}>
                      <Building size={14} className="text-muted" />
                      <span><strong>{d.bank}</strong> — {d.no_rekening || '-'}</span>
                    </div>
                  )}
                  <div className="region-detail">
                    <CreditCard size={14} className="text-muted" />
                    <strong>Pembayaran:</strong> {d.metode_pembayaran || 'Transfer'}
                  </div>
                </div>
                <div className="action-btns" style={{ padding: '10px 16px', borderTop: '1px solid var(--gray-100)', justifyContent: 'flex-end' }}>
                  <button className="btn-icon" title="Edit" onClick={() => openEdit(d.id)}><Edit2 size={14} /></button>
                  <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d.id)}><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: editing ? 640 : 1280 }}>
            <div className="modal-header" style={{
              background: isMingguan ? 'linear-gradient(135deg, #8965e0 0%, #5b3fc4 100%)' : 'linear-gradient(135deg, var(--cyan-500) 0%, var(--cyan-700) 100%)',
              margin: -1, padding: '20px 24px', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              display: 'flex', alignItems: 'center', gap: 12
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DollarSign size={18} color="white" />
              </div>
              <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
                {editing ? 'Edit Komponen Gaji' : 'Tambah Komponen Gaji (Input Excel)'}
              </h3>
            </div>
            <div className="modal-body">
              {/* ===== MODE GRID EXCEL (tambah baru) ===== */}
              {!editing && (
                <div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    background: 'var(--gray-50)', border: '1px solid var(--gray-100)',
                    borderRadius: 10, padding: '12px 14px', marginBottom: 14,
                  }}>
                    <div className="form-group" style={{ margin: 0, minWidth: 220 }}>
                      <label>Jenis Penggajian <span style={{ color: 'var(--danger)' }}>*</span></label>
                      <select value={gridJenis} onChange={e => { setGridJenis(e.target.value); setGridRows(gridRows.map(r => ({ ...r }))); }}>
                        <option value="Bulanan">Bulanan</option>
                        <option value="Mingguan">Mingguan</option>
                      </select>
                    </div>
                    <div style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--gray-500)', textAlign: 'right' }}>
                      Isi seperti Excel — 1 baris = 1 karyawan.<br />
                      Klik <strong>Simpan Semua</strong> untuk menyimpan sekaligus.
                    </div>
                  </div>

                  <div className="table-wrapper" style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
                    <table className="data-table excel-grid" style={{ minWidth: 900 }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                        <tr>
                          <th style={{ minWidth: 200 }}>Karyawan</th>
                          {gridJenis === 'Mingguan' ? (
                            <>
                              <th style={{ minWidth: 120 }}>Transport BBM</th>
                              <th style={{ minWidth: 120 }}>Sewa Kendaraan</th>
                            </>
                          ) : (
                            <>
                              <th style={{ minWidth: 120 }}>Gaji Pokok</th>
                              <th style={{ minWidth: 110 }}>Tunj. Jabatan</th>
                              <th style={{ minWidth: 110 }}>Insentif AC</th>
                              <th style={{ minWidth: 110 }}>Insentif Project</th>
                              <th style={{ minWidth: 100 }}>Pot. Alpa</th>
                              <th style={{ minWidth: 100 }}>Pot. Kasbon</th>
                            </>
                          )}
                          <th style={{ minWidth: 170 }}>Keterangan</th>
                          <th style={{ minWidth: 110 }}>Total</th>
                          <th style={{ width: 40 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {gridRows.map((row, idx) => {
                          const total = gridJenis === 'Mingguan'
                            ? (Number(row.tunjangan_transport_bbm) || 0) + (Number(row.tunjangan_sewa_kendaraan) || 0)
                            : (Number(row.gaji_pokok) || 0) + (Number(row.tunjangan_jabatan) || 0) + (Number(row.insentif_keahlian_ac) || 0)
                              + (Number(row.insentif_project) || 0) - (Number(row.potongan_alpa) || 0) - (Number(row.potongan_kasbon) || 0);
                          return (
                            <tr key={idx}>
                              <td>
                                <select value={row.karyawan_id} onChange={e => updateGridRow(idx, 'karyawan_id', e.target.value)}
                                  style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.8rem' }}>
                                  <option value="">-- Pilih Karyawan --</option>
                                  {karyawan.map(k => (
                                    <option key={k.id} value={k.id}>
                                      {k.nama}{k.bagian_name ? ` — ${k.bagian_name}` : ''}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              {gridJenis === 'Mingguan' ? (
                                <>
                                  <td><input type="number" value={row.tunjangan_transport_bbm} onChange={e => updateGridRow(idx, 'tunjangan_transport_bbm', e.target.value)} placeholder="0" style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.8rem', textAlign: 'right' }} /></td>
                                  <td><input type="number" value={row.tunjangan_sewa_kendaraan} onChange={e => updateGridRow(idx, 'tunjangan_sewa_kendaraan', e.target.value)} placeholder="0" style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.8rem', textAlign: 'right' }} /></td>
                                </>
                              ) : (
                                <>
                                  <td><input type="number" value={row.gaji_pokok} onChange={e => updateGridRow(idx, 'gaji_pokok', e.target.value)} placeholder="0" style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.8rem', textAlign: 'right' }} /></td>
                                  <td><input type="number" value={row.tunjangan_jabatan} onChange={e => updateGridRow(idx, 'tunjangan_jabatan', e.target.value)} placeholder="0" style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.8rem', textAlign: 'right' }} /></td>
                                  <td><input type="number" value={row.insentif_keahlian_ac} onChange={e => updateGridRow(idx, 'insentif_keahlian_ac', e.target.value)} placeholder="0" style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.8rem', textAlign: 'right' }} /></td>
                                  <td><input type="number" value={row.insentif_project} onChange={e => updateGridRow(idx, 'insentif_project', e.target.value)} placeholder="0" style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.8rem', textAlign: 'right' }} /></td>
                                  <td><input type="number" value={row.potongan_alpa} onChange={e => updateGridRow(idx, 'potongan_alpa', e.target.value)} placeholder="0" style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.8rem', textAlign: 'right' }} /></td>
                                  <td><input type="number" value={row.potongan_kasbon} onChange={e => updateGridRow(idx, 'potongan_kasbon', e.target.value)} placeholder="0" style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.8rem', textAlign: 'right' }} /></td>
                                </>
                              )}
                              <td>
                                <input type="text" value={row.keterangan} onChange={e => updateGridRow(idx, 'keterangan', e.target.value)} placeholder="Catatan..." style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.8rem' }} />
                              </td>
                              <td style={{ fontWeight: 700, color: total >= 0 ? 'var(--cyan-700)' : '#f5365c', fontSize: '0.82rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {fmtRupiah(total)}
                              </td>
                              <td>
                                <button className="btn-icon btn-danger" onClick={() => removeGridRow(idx)} disabled={gridRows.length <= 1}><Trash2 size={13} /></button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
                    <button className="btn btn-secondary" style={{ fontSize: '0.8rem', padding: '6px 14px' }} onClick={addGridRow}>
                      <Plus size={14} /> Tambah Baris
                    </button>
                    <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>
                      {gridRows.filter(r => r.karyawan_id).length} karyawan siap disimpan · total {fmtRupiah(gridRows.reduce((s, r) => s + (gridJenis === 'Mingguan'
                        ? (Number(r.tunjangan_transport_bbm) || 0) + (Number(r.tunjangan_sewa_kendaraan) || 0)
                        : (Number(r.gaji_pokok) || 0) + (Number(r.tunjangan_jabatan) || 0) + (Number(r.insentif_keahlian_ac) || 0) + (Number(r.insentif_project) || 0) - (Number(r.potongan_alpa) || 0) - (Number(r.potongan_kasbon) || 0)), 0))}
                    </span>
                  </div>
                </div>
              )}

              {/* ===== MODE EDIT (form lama, satu karyawan) ===== */}
              {editing && <div style={{
                background: 'var(--accent-tint-bg)', borderRadius: 'var(--radius)',
                border: '1px solid var(--cyan-100)', overflow: 'hidden',
              }}>
                {/* Jenis Penggajian */}
                <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px', borderBottom: '1px solid var(--gray-100)' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Karyawan <span style={{color:'var(--danger)'}}>*</span></label>
                    <select value={form.karyawan_id} onChange={e => setForm({...form, karyawan_id: parseInt(e.target.value)})} required>
                      <option value="">Pilih Karyawan</option>
                      {karyawan.map(k => <option key={k.id} value={k.id}>{k.nama}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Jenis Penggajian <span style={{color:'var(--danger)'}}>*</span></label>
                    <select value={form.jenis_gaji} onChange={e => setForm({...form, jenis_gaji: e.target.value})}>
                      <option value="Bulanan">Bulanan</option>
                      <option value="Mingguan">Mingguan</option>
                    </select>
                  </div>
                </div>

                {!isMingguan ? (
                  <>
                    {/* Section: Komponen Gaji Bulanan */}
                    <div style={{
                      padding: '12px 16px', borderBottom: '1px solid var(--cyan-100)',
                      background: '#fff',
                      fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                      letterSpacing: '0.5px', color: 'var(--cyan-700)',
                      textAlign: 'center',
                    }}>
                      <DollarSign size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                      Komponen Gaji Bulanan
                    </div>
                    <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Gaji Pokok <span style={{color:'var(--danger)'}}>*</span></label>
                        <input type="number" value={form.gaji_pokok} onChange={e => setForm({...form, gaji_pokok: parseFloat(e.target.value) || 0})} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Tunjangan Jabatan</label>
                        <input type="number" value={form.tunjangan_jabatan} onChange={e => setForm({...form, tunjangan_jabatan: parseFloat(e.target.value) || 0})} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Insentif Keahlian (AC)</label>
                        <input type="number" value={form.insentif_keahlian_ac} onChange={e => setForm({...form, insentif_keahlian_ac: parseFloat(e.target.value) || 0})} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Insentif Project</label>
                        <input type="number" value={form.insentif_project} onChange={e => setForm({...form, insentif_project: parseFloat(e.target.value) || 0})} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Potongan Alpa</label>
                        <input type="number" value={form.potongan_alpa} onChange={e => setForm({...form, potongan_alpa: parseFloat(e.target.value) || 0})} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Potongan Kasbon</label>
                        <input type="number" value={form.potongan_kasbon} onChange={e => setForm({...form, potongan_kasbon: parseFloat(e.target.value) || 0})} />
                      </div>
                    </div>

                    {/* Section: BPJS */}
                    <div style={{
                      padding: '12px 16px', borderTop: '1px solid var(--cyan-100)', borderBottom: '1px solid var(--cyan-100)',
                      background: '#fff',
                      fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                      letterSpacing: '0.5px', color: 'var(--cyan-700)',
                      textAlign: 'center',
                    }}>
                      BPJS (opsional)
                    </div>
                    <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>BPJS Kesehatan (Karyawan)</label>
                        <input type="number" value={form.bpjs_kesehatan} onChange={e => setForm({...form, bpjs_kesehatan: parseFloat(e.target.value) || 0})} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>BPJS Ketenagakerjaan (Karyawan)</label>
                        <input type="number" value={form.bpjs_ketenagakerjaan} onChange={e => setForm({...form, bpjs_ketenagakerjaan: parseFloat(e.target.value) || 0})} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>BPJS Kesehatan (Perusahaan)</label>
                        <input type="number" value={form.bpjs_kesehatan_perusahaan} onChange={e => setForm({...form, bpjs_kesehatan_perusahaan: parseFloat(e.target.value) || 0})} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>BPJS Ketenagakerjaan (Perusahaan)</label>
                        <input type="number" value={form.bpjs_ketenagakerjaan_perusahaan} onChange={e => setForm({...form, bpjs_ketenagakerjaan_perusahaan: parseFloat(e.target.value) || 0})} />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Section: Komponen Gaji Mingguan */}
                    <div style={{
                      padding: '12px 16px', borderBottom: '1px solid var(--cyan-100)',
                      background: '#fff',
                      fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                      letterSpacing: '0.5px', color: '#5b3fc4',
                      textAlign: 'center',
                    }}>
                      <CalendarDays size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                      Komponen Gaji Mingguan
                    </div>
                    <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Tunjangan Transport BBM</label>
                        <input type="number" value={form.tunjangan_transport_bbm} onChange={e => setForm({...form, tunjangan_transport_bbm: parseFloat(e.target.value) || 0})} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Tunjangan Sewa Kendaraan</label>
                        <input type="number" value={form.tunjangan_sewa_kendaraan} onChange={e => setForm({...form, tunjangan_sewa_kendaraan: parseFloat(e.target.value) || 0})} />
                      </div>
                    </div>
                  </>
                )}

                {/* Section: Data Pembayaran */}
                <div style={{
                  padding: '12px 16px', borderTop: '1px solid var(--cyan-100)', borderBottom: '1px solid var(--cyan-100)',
                  background: '#fff',
                  fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.5px', color: 'var(--cyan-700)',
                  textAlign: 'center',
                }}>
                  <CreditCard size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  Data Pembayaran
                </div>
                <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Metode Pembayaran</label>
                    <select value={form.metode_pembayaran} onChange={e => setForm({...form, metode_pembayaran: e.target.value})}>
                      <option value="Transfer">Transfer</option>
                      <option value="Tunai">Tunai</option>
                      <option value="Cek">Cek</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Bank</label>
                    <input type="text" value={form.bank} onChange={e => setForm({...form, bank: e.target.value})} placeholder="Nama Bank" />
                  </div>
                  <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                    <label>No. Rekening</label>
                    <input type="text" value={form.no_rekening} onChange={e => setForm({...form, no_rekening: e.target.value})} placeholder="Nomor rekening bank" />
                  </div>
                  <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                    <label>Keterangan</label>
                    <textarea rows={2} value={form.keterangan} onChange={e => setForm({...form, keterangan: e.target.value})} placeholder="Catatan tambahan (opsional) — tidak memengaruhi total gaji" />
                  </div>
                </div>
              </div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Batal</button>
              {editing ? (
                <button className="btn btn-primary" onClick={handleSubmit}>Simpan</button>
              ) : (
                <button className="btn btn-primary" onClick={handleBatchSubmit}>
                  <Plus size={15} style={{ marginRight: 4, verticalAlign: -2 }} /> Simpan Semua
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}